import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  buildFindingsPath,
  buildProjectSearch,
  buildPullRequestsPath,
} from '@/lib/routes';

/**
 * Cross-route navigation between the two findings destinations: Backlog
 * (`/$org/findings`, repo-wide drift) and Pull Requests (`/$org/pull-requests`,
 * the PR review inbox). The split replaced the old `scope` search param — the
 * switch now changes the URL *path*, not a query key. Both targets carry the
 * active project through `buildProjectSearch` so the destination opens on the
 * same project.
 */
export function useFindingsScopeNav() {
  const navigate = useNavigate();
  const { org } = useOrgContext();
  const { resolvedSlug } = useProjectContext();
  const orgSlug = org?.slug ?? null;

  return React.useMemo(() => {
    const search = buildProjectSearch(resolvedSlug);
    return {
      goToBacklog: () => {
        if (!orgSlug) return;
        navigate({ to: buildFindingsPath(orgSlug), search } as never);
      },
      goToPullRequests: () => {
        if (!orgSlug) return;
        navigate({ to: buildPullRequestsPath(orgSlug), search } as never);
      },
    };
  }, [navigate, orgSlug, resolvedSlug]);
}
